import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Point } from 'highcharts';
import Highcharts, { addEvent, removeEvent } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import type { BoxPlotPoint, StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import {
  adjustColor,
  destroySVGElement,
  drawPointLabel,
  generateScatterFromBins,
  getBoxWidth,
  getLabelPoints,
  truncateValue,
} from '@/summaryReport/summaryReport.utilities';
import { useTranslation } from 'react-i18next';

interface SummaryReportBoxPlotProps {
  items: StatisticsSummaryItem[];
  title?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  isOverlay?: boolean;
  showLabels?: boolean;
}

interface PointBoxplot extends Point {
  q1: number;
  median: number;
  q3: number;
  low: number;
  high: number;
  x: number;
  y: number;
}

export const SummaryReportBoxPlot: React.FunctionComponent<SummaryReportBoxPlotProps> = ({
  items,
  title,
  xAxisTitle,
  yAxisTitle,
  isOverlay,
  showLabels,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [scatterPoints, setScatterPoints] = useState<any[]>([]);

  const OVERLAY_DIVIDER = 2;
  const STANDARD_DIVIDER = 4;

  const overallMin = Math.min(...items.map((item) => Math.min(item.data.stats.min, item.data.stats.quartile0)));
  const overallMax = Math.max(...items.map((item) => Math.max(item.data.stats.max, item.data.stats.quartile4)));
  const isMultiple = items.length > 1;
  const categories = items.map((item) => item.name);

  const boxPlotData = items.map((item, idx) => ({
    x: idx,
    low: item.data.stats.minWhisker,
    q1: item.data.stats.quartile1,
    median: item.data.stats.quartile2,
    q3: item.data.stats.quartile3,
    high: item.data.stats.maxWhisker,
    color: item.color,
    fillColor: adjustColor(item.color, 70),
  }));

  function getMinWhiskerValue(item: StatisticsSummaryItem) {
    return {
      label: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER'),
      value: truncateValue(item.data.stats.minWhisker),
    };
  }

  function getMaxWhiskerValue(item: StatisticsSummaryItem) {
    return {
      label: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER'),
      value: truncateValue(item.data.stats.maxWhisker),
    };
  }

  const tooltipFormatter = function (this: PointBoxplot) {
    const item = items[this.x];
    const minWhisker = getMinWhiskerValue(item);
    const maxWhisker = getMaxWhiskerValue(item);
    const q1 = truncateValue(this.q1);
    const median = truncateValue(this.median);
    const q3 = truncateValue(this.q3);
    const uom = item.data.uom ? ` (${item.data.uom})` : '';

    return (
      `<b>${this.category as string}</b><br/>` +
      `<span>${minWhisker.label}</span>: <span>${minWhisker.value}${uom}</span><br/>` +
      `<span>${t('INVESTIGATE_TOOLS.SUMMARY_REPORT.Q1')}</span>: <span>${q1}${uom}</span><br/>` +
      `<span>${t('INVESTIGATE_TOOLS.SUMMARY_REPORT.MEDIAN')}</span>: <span>${median}${uom}</span><br/>` +
      `<span>${t('INVESTIGATE_TOOLS.SUMMARY_REPORT.Q3')}</span>: <span>${q3}${uom}</span><br/>` +
      `<span>${maxWhisker.label}</span>: <span>${maxWhisker.value}${uom}</span>`
    );
  };

  const outlierTooltipFormatter = function (this: Point & { uom?: string }) {
    const uom = this.uom ? `(${this.uom})` : '';
    return `${truncateValue(this.y as number)} ${uom}`;
  };

  const destroyPoints = (points: Record<string, BoxPlotPoint[]>) =>
    Object.values(points).forEach((pointsArr) => {
      pointsArr.forEach((point) => {
        destroySVGElement(point?.label);
        destroySVGElement(point?.circle);
      });
    });

  useEffect(() => {
    const chart = chartRef.current?.chart;
    const points = getLabelPoints(items);

    function updateScatter() {
      if (chart) {
        const allScatterPoints = items.flatMap((item, idx) => {
          return generateScatterFromBins(item, idx, chart, isOverlay as boolean);
        });
        setScatterPoints(allScatterPoints);
      } else {
        setScatterPoints([]);
      }
    }

    function redrawCustomGraphics() {
      destroyPoints(points);

      if (chart && showLabels) {
        items.forEach((item, idx) => {
          const halfWidth = getBoxWidth(chart, idx) / 2;
          const lastPosition = {
            x: -Infinity,
            isMulti: isMultiple,
            offset: halfWidth,
          };
          points[item.id]?.forEach((point) => {
            const ret = drawPointLabel(chart, point, idx, lastPosition);
            lastPosition.x = ret.lastX || lastPosition.x;
            lastPosition.offset = ret.labelOffset || 0;
          });
        });
      }
    }

    if (chart) {
      redrawCustomGraphics();
      updateScatter();
      addEvent(chart, 'redraw', redrawCustomGraphics);
      addEvent(chart, 'resize', redrawCustomGraphics);
      addEvent(chart, 'resize', updateScatter);
    }

    return () => {
      if (chart) {
        destroyPoints(points);
        removeEvent(chart, 'redraw', redrawCustomGraphics);
        removeEvent(chart, 'resize', redrawCustomGraphics);
        removeEvent(chart, 'resize', updateScatter);
      }
    };
  }, [items, chartRef, showLabels, isOverlay, isMultiple]);

  const series: Highcharts.SeriesOptionsType[] = [
    {
      name: 'Boxplot',
      type: 'boxplot',
      data: boxPlotData,
      tooltip: {
        headerFormat: '',
        pointFormatter: tooltipFormatter,
      },
    } as Highcharts.SeriesBoxplotOptions,
    {
      name: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.OUTLIER'),
      type: 'scatter',
      data: scatterPoints,
      tooltip: {
        pointFormatter: outlierTooltipFormatter,
      },
    } as Highcharts.SeriesScatterOptions,
  ];

  const options: Highcharts.Options = {
    chart: {
      type: 'boxplot',
      inverted: true,
      height: isOverlay ? '80%' : '200px',
      animation: false,
      marginLeft: 40,
      marginRight: 12,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: 'small',
        fontWeight: 'normal',
      },
    },
    xAxis: {
      categories,
      visible: false,
      title: {
        text: xAxisTitle,
      },
    },
    yAxis: {
      title: {
        text: yAxisTitle,
      },
      min: overallMin,
      max: overallMax,
    },
    plotOptions: {
      boxplot: {
        animation: false,
        whiskerLength: '100%',
      },
      scatter: {
        animation: false,
      },
      series: {
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    series,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />;
};
